import { REVERB_APP_KEY, REVERB_HOST, REVERB_PORT, REVERB_SCHEME } from '@/config';
import { axios } from '@/lib/axios';
import { RealtimeManager } from '@/providers/realtime-manager';
import { configureEcho, echo, echoIsConfigured } from '@laravel/echo-react';

import { createContext, ReactNode, useContext, useMemo, useRef } from 'react';

type RealtimeProviderProps = {
  children: ReactNode;
};

type RealtimeContextData = {
  manager: RealtimeManager;
};

const RealtimeContext = createContext<RealtimeContextData | null>(null);

export const useRealtimeManager = () => {
  const context = useContext(RealtimeContext);

  if (!context) {
    throw new Error('useRealtimeManager must be used within a RealtimeProvider.');
  }

  return context;
};

export const RealtimeProvider = ({ children }: RealtimeProviderProps) => {
  const managerRef = useRef<RealtimeManager>();

  if (!managerRef.current) {
    managerRef.current = new RealtimeManager({ isDev: false });
  }

  if (!echoIsConfigured()) {
    configureEcho({
      key: REVERB_APP_KEY,
      wsHost: REVERB_HOST,
      wsPort: REVERB_PORT,
      wssPort: REVERB_PORT,
      forceTLS: (REVERB_SCHEME ?? 'https') === 'https',
      broadcaster: 'reverb',

      // For Sanctum + private channels, use Axios so cookies are included.
      authorizer: (channel: any) => ({
        authorize: async (socketId: string, callback: any) => {
          try {
            const payload = {
              channel_name: channel.name,
              socket_id: socketId,
            };

            const authResponse = (await axios.post('/broadcasting/auth', payload)) as any;
            if (authResponse?.auth) {
              callback(null, authResponse);
              return;
            }

            callback(new Error('Broadcast authentication failed.'), null);
          } catch (error) {
            callback(error as Error, null);
          }
        },
      }),
    });
  }

  managerRef.current.setEcho(echo());

  const value = useMemo(
    () => ({
      manager: managerRef.current as RealtimeManager,
    }),
    [],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};
